import React, {type PropsWithChildren} from 'react';
import type {SuggestionProps} from '@tiptap/suggestion';

import {Avatar} from '@time-webkit/all/atoms/avatar';
import {AccountMultipleIcon} from '@time-webkit/all/icons/account-multiple-outline';
import {MegaphoneIcon} from '@time-webkit/all/icons/megaphone';
import classNames from 'classnames';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {sendToStatist} from '@time-webkit/statist';

import GuestBadge from 'components/widgets/badges/guest_badge';
import CustomStatusEmoji from 'components/custom_status/custom_status_emoji';

import {isGuest, isGuestPlus} from 'mattermost-redux/utils/user_utils';

import GuestPlusBadge from 'components/widgets/badges/guest_plus_badge';

import BotBadge from 'components/widgets/badges/bot_badge';
import {type IEditor} from 'packages/editor';

import {MentionList} from '../../../../components/mention-list';

import type {CustomMention, GroupMention, Mention, UserMention} from '../../types';

import styles from './styles.module.css';

type MentionListProps = {
    getMentions: (query: string) => Promise<Mention[]>;
    editor?: IEditor;
    hideLoader?: boolean;
} & Pick<SuggestionProps<Mention>, 'query' | 'command' | 'decorationNode'>;

const Description: React.FC<PropsWithChildren & {className?: string}> = (props) => {
    const typographyCls = useTypography({
        size: TypographySize.BodyS,
        noDefaultColor: true,
    });

    return <span className={classNames(styles.description, props.className, typographyCls)}>{props.children}</span>;
};

const Handle: React.FC<PropsWithChildren & {className?: string}> = (props) => {
    const typographyCls = useTypography({
        size: TypographySize.BodyL,
    });

    return <span className={classNames(styles.handle, props.className, typographyCls)}>{props.children}</span>;
};

const UserOption = (props: UserMention) => {
    const hasNameDescription = props.displayName !== props.handle;
    return (
        <div className={styles.mentionWrapper}>
            <Avatar
                src={props.imageUrl}
                text={props.displayName}
                username={props.handle}
                size={24}
                inline={true}
                className={styles.shrink}
            />
            <Handle className={classNames(styles.basePriority, styles.minPriority)}>{props.displayName}</Handle>
            <CustomStatusEmoji
                userID={props.data.id}
                showTooltip={true}
                emojiStyle={{marginLeft: '-4px'}}
                spanStyle={{marginTop: '-2px'}}
            />

            {hasNameDescription ? (
                <Description className={classNames(styles.basePriority, styles.maxPriority, styles.mtBase)}>
                    {`@${props.handle}`}
                </Description>
            ) : null}
            <GuestPlusBadge
                show={isGuestPlus(props.data.roles) && !props.data.is_bot}
                className={styles.shrink}
            />
            <GuestBadge
                show={isGuest(props.data.roles) && !props.data.is_bot}
                className={styles.shrink}
            />
            <BotBadge show={Boolean(props.data.is_bot)} className={styles.shrink} />
        </div>
    );
};

const GroupOption = (props: GroupMention | CustomMention) => {
    const hasNameDescription = props.displayName !== props.handle;
    const icon = props.type === 'custom' ? <MegaphoneIcon /> : <AccountMultipleIcon />;
    const descCls = props.type === 'custom' ? styles.mtCustom : styles.mtBase;

    return (
        <div className={styles.mentionWrapper}>
            <span className={classNames(styles.groupIcon, styles.shrink)}>
                {icon}
            </span>
            <Handle className={classNames(styles.basePriority, styles.maxPriority)}>{`@${props.handle}`}</Handle>
            {hasNameDescription ? (
                <Description className={classNames(styles.basePriority, styles.minPriority, descCls)}>
                    {props.displayName}
                </Description>
            ) : null}
        </div>
    );
};

const MentionOption = (props: Mention) => {
    const displayName = props.displayName;

    if (props.type === 'user') {
        return <UserOption {...props} />;
    }

    if (props.type === 'group') {
        return <GroupOption {...props} />;
    }

    if (props.type === 'custom') {
        const groupProps = props as unknown as GroupMention;
        return <GroupOption {...groupProps} />;
    }

    return <span>{displayName}</span>;
};

export const UserMentionsList: React.FC<MentionListProps> = (props) => {
    const {query, getMentions, command, decorationNode, hideLoader} = props;
    const onSelect = (_: Mention, index: number, params?: Record<string, unknown>) => {
        sendToStatist('editor.searchUser.resultTap', {
            resultNumber: index + 1,
            ...params,
        });
    };

    return (
        <MentionList
            query={query}
            command={command}
            decorationNode={decorationNode}
            getMentions={getMentions}
            renderMentionOption={(item) => <MentionOption {...item} />}
            onSelect={onSelect}
            ignoreEmptyQuery={false}
            editor={props.editor}
            shouldRequestOnFocus={true}
            hideLoader={hideLoader}
            resetOnClose={true}
        />
    );
};
